// ============================================================
// NewCaseForClientModal.tsx — "New case" from a client's page (B2).
// The client is known, so the only question is the REQUIRED claim type
// (+ its suggested route — ClaimTypeFields). The parent creates the case
// (createCaseApi) and opens it; a server refusal of the claim type comes
// back as `claimError` and shows under the field.
// ============================================================
import React, { useEffect, useState } from 'react'
import ModalFrame from '../shared/ModalFrame'
import ClaimTypeFields, { CLAIM_TYPE_MISSING, EMPTY_CLAIM_CHOICE, type ClaimChoice } from './ClaimTypeFields'
import { BTN_PRIMARY } from '../../styles/buttons'
import { BTN_CANCEL } from './caseEntryStyles'
import type { ClaimRoute, ClaimType } from '../../utils/claimTypes'

interface NewCaseForClientModalProps {
  isOpen: boolean
  clientName: string
  busy: boolean
  /** The server's refusal of the claim type / route, or a general error. */
  claimError?: string | null
  error?: string | null
  onClose: () => void
  onCreate: (claim: { claimType: ClaimType; claimRoute: ClaimRoute | null }) => void
}

const NewCaseForClientModal: React.FC<NewCaseForClientModalProps> = ({ isOpen, clientName, busy, claimError, error, onClose, onCreate }) => {
  const [claim, setClaim] = useState<ClaimChoice>(EMPTY_CLAIM_CHOICE)
  const [missing, setMissing] = useState(false)
  useEffect(() => {
    if (!isOpen) return
    setClaim(EMPTY_CLAIM_CHOICE)
    setMissing(false)
  }, [isOpen])

  const submit = (): void => {
    if (busy) return
    if (!claim.claimType) { setMissing(true); return }
    onCreate({ claimType: claim.claimType, claimRoute: claim.claimRoute || null })
  }

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      dismissible={!busy}
      eyebrow="New case"
      title={`New case for ${clientName}`}
      subtitle="Choose the claim type. Everything else can be filled in on the case."
      footerRight={
        <>
          <button type="button" onClick={onClose} disabled={busy} className={BTN_CANCEL}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className={`${BTN_PRIMARY} text-sm rounded-lg px-5 py-2.5`} aria-busy={busy}>
            {busy ? 'Creating…' : 'Create case'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); submit() }}>
        <ClaimTypeFields
          idPrefix="ncc"
          value={claim}
          onChange={(next) => { setClaim(next); if (next.claimType) setMissing(false) }}
          error={missing ? CLAIM_TYPE_MISSING : claimError}
          disabled={busy}
        />
        {error && <div role="alert" className="mt-4 px-3 py-2 rounded-lg text-xs bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]">{error}</div>}
      </form>
    </ModalFrame>
  )
}

export default NewCaseForClientModal
